import type { OfferWrapper, Provider } from 'generated/api'
import Ribbon from 'components/domain/search/offers/loans/LoanOfferCard/components/Ribbon'
import { EmptySVG, PreApprovedIcon, SimpleOfferIcon } from 'components/domain/search/offers/common/icons/icons'
import {
	getSkeletonTrivia,
	getTriviaFromOffer
} from 'components/domain/search/offers/loans/LoanOfferCard/components/Trivia'
import { noOp } from 'lib'
import { ThumborImg } from 'lib/images/ThumborImg'
import { Pending, PendingStatus } from 'components/patterns/suspense/internals/types'
import { VoidCallback } from 'types/index'
import { Button, Skeleton } from 'reshaped/bundle'
import { getSearchTriviaFromOffer } from 'components/domain/search/offers/loans/LoanOfferCard/components/SearchOfferCardTrivia'
import { cnfSearchRedirectLink } from 'experiences/explore/loans/components/NewLoanSearchWidget/config'

export interface LoanOfferCardBlocks {
	ribbon: JSX.Element
	image: JSX.Element
	trivia: Array<JSX.Element>
	button: JSX.Element
	disclosure: JSX.Element
	status: PendingStatus
	onClick?: VoidCallback
}

interface BuilderProps {
	offer: OfferWrapper
	provider: Provider
	onClick?: VoidCallback
}

const getIcon = (val?: string): (() => JSX.Element) => {
	switch (val) {
		case 'star':
			return PreApprovedIcon
		case 'special':
			return SimpleOfferIcon
		default:
			return EmptySVG
	}
}

export const buildLoanBlocks = (offerDetails: Pending<BuilderProps>): LoanOfferCardBlocks => {
	if (offerDetails.pending) {
		return {
			button: <Skeleton width="96px" height="48px" />,
			image: <Skeleton width="120px" height="30px" />,
			ribbon: <Ribbon pending={true} />,
			trivia: getSkeletonTrivia(),
			disclosure: <></>,
			status: PendingStatus.loading,
			onClick: noOp
		}
	} else {
		const offer = offerDetails.offer
		const provider = offerDetails.provider
		const buttonProps = { href: cnfSearchRedirectLink.search }
		return {
			button: (
				<Button color="positive" size="large" {...buttonProps}>
					{offer?.display?.ctaText || 'Continue'}
				</Button>
			),
			image: (
				<ThumborImg
					src={offer.image}
					alt={offerDetails.provider.name}
					options={{ width: 110, height: 30 }}
					style={{ width: '110px', height: '30px', objectFit: 'contain', objectPosition: 'left' }}
				/>
			),
			ribbon: (
				<Ribbon
					categoryDetails={offer.categoryDetails!}
					approvalIcon={getIcon(offer.display?.ribbonIcon)}
					approvalText={offerDetails.offer.display?.ribbonText}
				/>
			),
			trivia: getTriviaFromOffer(offer),
			disclosure: (
				<>
					<p>Offer Details</p>
					<div dangerouslySetInnerHTML={{ __html: provider?.description || '' }} />
					<p className="leadText">Disclaimer</p>
					<div dangerouslySetInnerHTML={{ __html: provider?.disclaimer }} />
				</>
			),
			status: PendingStatus.complete,
			onClick: offerDetails.onClick
		}
	}
}

export const buildSearchHistoryBlocks = (offerDetails: Pending<BuilderProps>): LoanOfferCardBlocks => {
	if (offerDetails.pending) {
		return {
			button: <Skeleton width="96px" height="48px" />,
			image: <Skeleton width="120px" height="30px" />,
			ribbon: <Ribbon pending={true} />,
			trivia: getSkeletonTrivia(),
			disclosure: <></>,
			status: PendingStatus.loading,
			onClick: noOp
		}
	} else {
		const offer = offerDetails.offer
		const provider = offerDetails.provider
		const buttonProps = offerDetails.onClick ? { onClick: offerDetails.onClick } : { href: offer.url }

		return {
			button: (
				<Button color="positive" size="large" {...buttonProps}>
					{offer?.display?.ctaText || 'Continue'}
				</Button>
			),
			image: (
				<ThumborImg
					src={offer.image}
					alt={offerDetails.provider.name}
					options={{ width: 110, height: 30 }}
					style={{ width: '110px', height: '30px', objectFit: 'contain', objectPosition: 'left' }}
				/>
			),
			ribbon: (
				<Ribbon
					categoryDetails={offer.categoryDetails!}
					approvalIcon={getIcon(offer.display?.ribbonIcon)}
					approvalText={offerDetails.offer.display?.ribbonText}
				/>
			),
			trivia: getSearchTriviaFromOffer(offer),
			disclosure: (
				<>
					<p>Offer Details</p>
					<div dangerouslySetInnerHTML={{ __html: provider?.description || '' }} />
					<p className="leadText">Disclaimer</p>
					<div dangerouslySetInnerHTML={{ __html: provider?.disclaimer }} />
				</>
			),
			status: PendingStatus.complete,
			onClick: offerDetails.onClick
		}
	}
}
